import { useEffect } from "react";
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/modules/common';
import { MultiSelect } from '@/modules/common/components/multi-select';
import { OptionType } from '@/modules/common/types';

import { useCreateEvent } from '../../api';
import { addEventSchema } from './schema';

interface IProps {
  isOpen: boolean;
  typesOptions: OptionType[];
  onOpenChange: (open: boolean) => void;
}

export type AddEventFormValues = z.infer<typeof addEventSchema>;

export function AddEventDialog({ isOpen, onOpenChange, typesOptions }: IProps) {
  const { createEventMutation, uploadUrlData } = useCreateEvent();

  useEffect(() => {
    if (!isOpen) form.reset();
  }, [isOpen]);

  const form = useForm<AddEventFormValues>({
    resolver: zodResolver(addEventSchema),
    defaultValues: {
      types: [],
      name: '',
      startDate: '',
    },
  });

  const handleSubmit = async (values: AddEventFormValues) => {
    try {
      await createEventMutation.mutateAsync({
        id: uploadUrlData.eventId,
        name: values.name,
        startDate: values.startDate,
        types: values.types.map((type) => ({ typeId: type })),
      });

      onOpenChange(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <>
      <Dialog onOpenChange={onOpenChange} open={isOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Event</DialogTitle>
            <DialogDescription>Description</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                form.handleSubmit(handleSubmit)(event);
              }}
            >
              <div className="flex flex-col gap-4">
                <MultiSelect
                  name="types"
                  label="Type"
                  control={form.control}
                  placeholder="Select event types"
                  options={typesOptions}
                  maxCount={4}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter event name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DatePicker
                  control={form.control}
                  name="startDate"
                  label="Start date"
                />
              </div>
              <DialogFooter className="mt-6 gap-4">
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-button">
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  data-testid="save-button"
                  isLoading={createEventMutation.isPending}
                >
                  Add event
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
